import React, { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import config from '../../../config/environment';
import WhiteLogo from '../../../assets/white-logo.png';

const float = keyframes`
  0% { transform: translateY(0); opacity: 0.4; }
  50% { transform: translateY(-10px); opacity: 0.8; }
  100% { transform: translateY(0); opacity: 0.4; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: radial-gradient(1200px 600px at 50% -10%, rgba(21,33,70,0.85), rgba(11,19,41,1) 60%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 20px 40px;
  color: #fff;
`;

const Header = styled.header`
  position: absolute;
  top: 24px;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`;

const LogoWrap = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  padding: 8px 14px;
  border-radius: 12px;
  background: rgba(255,255,255,0.06);
  border: 1px solid rgba(255,255,255,0.12);
  box-shadow: 0 10px 30px rgba(0,0,0,0.25);
  pointer-events: auto;
`;

const LogoImg = styled.img`
  height: 40px;
  width: auto;
  display: block;
`;

const BrandText = styled.span`
  font-family: ${props => props.theme?.fonts?.main || 'inherit'};
  font-weight: 700;
  letter-spacing: 0.4px;
  font-size: 0.95rem;
  color: #fff;
  opacity: 0.95;
`;

const Card = styled.div`
  position: relative;
  width: 100%;
  max-width: 640px;
  padding: 56px 48px 44px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(12,18,38,0.92), rgba(12,18,38,0.88));
  backdrop-filter: blur(14px);
  box-shadow: 0 40px 80px rgba(3, 6, 22, 0.85), inset 0 0 0 1px rgba(255,255,255,0.06);

  @media (max-width: 640px) {
    padding: 36px 24px 28px;
  }
`;

const AccentOrb = styled.div`
  position: absolute;
  border-radius: 50%;
  filter: blur(2px);
  animation: ${float} 6s ease-in-out infinite;
  opacity: 0.35;

  &.orb-1 {
    width: 260px;
    height: 260px;
    top: -90px;
    right: -70px;
    background: rgba(255, 255, 255, 0.06);
  }

  &.orb-2 {
    width: 160px;
    height: 160px;
    bottom: -60px;
    left: -40px;
    background: rgba(238, 91, 47, 0.20);
    animation-delay: 1.5s;
  }
`;

const Title = styled.h1`
  margin: 0 0 12px;
  font-size: 2.6rem;
  letter-spacing: 0.5px;
  font-family: ${props => props.theme?.fonts?.main || 'inherit'};
  text-shadow: 0 6px 28px rgba(0,0,0,0.55);

  @media (max-width: 640px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  margin: 0 0 28px;
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.7;
  font-family: ${props => props.theme?.fonts?.body || 'inherit'};
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.95rem;
  margin-bottom: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-family: ${props => props.theme?.fonts?.main || 'inherit'};
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 18px;
  border-radius: 14px;
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent || 'rgba(238, 91, 47, 0.85)'};
    background: rgba(255, 255, 255, 0.14);
    box-shadow: 0 12px 32px rgba(7, 14, 36, 0.55);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 15px 18px;
  border-radius: 14px;
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.07);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme?.colors?.accent || 'rgba(238, 91, 47, 0.85)'};
    background: rgba(255, 255, 255, 0.14);
    box-shadow: 0 12px 32px rgba(7, 14, 36, 0.55);
  }
`;

const Button = styled.button`
  padding: 16px 18px;
  border-radius: 14px;
  border: none;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-size: 1rem;
  background: ${props => props.theme?.colors?.accent
    ? `linear-gradient(135deg, ${props.theme.colors.accent}, ${props.theme.colors.accent})`
    : 'linear-gradient(135deg, #f07c3e, #d43434)'};
  color: #fff;
  cursor: pointer;
  transition: transform 0.2s ease, opacity 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    opacity: 0.95;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const StatusMessage = styled.div`
  margin-top: 16px;
  padding: 16px 18px;
  border-radius: 12px;
  font-weight: 600;
  text-align: center;
  border: 1px solid ${props => (props.error ? 'rgba(229,62,62,0.45)' : 'rgba(72,187,120,0.45)')};
  background: ${props => (props.error ? 'rgba(229,62,62,0.12)' : 'rgba(72,187,120,0.12)')};
  color: ${props => (props.error ? '#ff9b9b' : '#8dffcd')};
  box-shadow: inset 0 0 0 1px rgba(255,255,255,0.04);
`;

const Hint = styled.span`
  display: block;
  margin-top: 6px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.65);
`;

const schools = [
  'Keyworth Choir',
  'Burton Joyce Brass Band',
  'Harlaxton/Denton Choir',
  'Orston Primary School',
  'Carlton Male Voice Choir',
  'Harrowby Singers',
  'Melton Band',
  'Oakham School',
  'Hickling Choir',
  'Bingham Community Band',
  'Global Harmony Choir',
  'Cranmer Singers',
  'Vintage Singers',
  'Other'
];

const ChoirDiscount = () => {
  const [formData, setFormData] = useState({
    parent_name: '',
    email: '',
    child_name: '',
    school: '',
    other_group: ''
  });
  const [status, setStatus] = useState({
    submitting: false,
    error: null,
    success: false,
    code: null,
    message: null
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, error: null, success: false, code: null, message: null });

    // If "Other" is selected, use the custom text as the school value
    const submissionData = {
      ...formData,
      school: formData.school === 'Other' ? formData.other_group.trim() : formData.school
    };

    // Validate that if "Other" is selected, the custom text is provided
    if (formData.school === 'Other' && !formData.other_group.trim()) {
      setStatus({
        submitting: false,
        error: 'Please enter the name of your choir group.',
        success: false,
        code: null,
        message: null
      });
      return;
    }

    try {
      const response = await fetch(`${config.apiEndpoint}/belvoir/v1/choir-discount`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(submissionData)
      });

      const data = await response.json();

      if (!response.ok || data.success === false) {
        throw new Error(data?.message || 'Unable to generate your discount code. Please try again.');
      }

      setStatus({
        submitting: false,
        error: null,
        success: true,
        code: data.code,
        message: data.message || 'Your discount code has been emailed to you.'
      });

      setFormData({
        parent_name: '',
        email: '',
        child_name: '',
        school: '',
        other_group: ''
      });
    } catch (error) {
      setStatus({
        submitting: false,
        error: error.message || 'Something went wrong. Please try again.',
        success: false,
        code: null,
        message: null
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Choir Discount | Belvoir Castle</title>
        <meta name="description" content="Register to receive the supporter discount when attending a Christmas choir performance." />
      </Helmet>
      <PageWrapper>
        <Header>
          <LogoWrap>
            <LogoImg src={WhiteLogo} alt="Belvoir Castle" />
          </LogoWrap>
        </Header>
        <Card>
          <AccentOrb className="orb-1" />
          <AccentOrb className="orb-2" />
          <Title>Choir Supporter Discount</Title>
          <Description>
            Families attending a Christmas choir performance can unlock an exclusive discount.
            Tell us who you're cheering on and we'll email your unique code straight away. For any questions, please email info@belvoircastle.com.
          </Description>

          <Form onSubmit={handleSubmit}>
            <div>
              <Label htmlFor="parent_name">Your full name</Label>
              <Input
                id="parent_name"
                name="parent_name"
                value={formData.parent_name}
                onChange={handleChange}
                placeholder="e.g. Sarah Thompson"
                required
              />
            </div>

            <div>
              <Label htmlFor="email">Email address for the code</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
              <Hint>We’ll send the discount code and choir updates here.</Hint>
            </div>

            <div>
              <Label htmlFor="child_name">Performer’s full name</Label>
              <Input
                id="child_name"
                name="child_name"
                value={formData.child_name}
                onChange={handleChange}
                placeholder="Child you’re supporting"
                required
              />
            </div>

            <div>
              <Label htmlFor="school">School or choir group</Label>
              <Select
                id="school"
                name="school"
                value={formData.school}
                onChange={handleChange}
                required
              >
                <option value="">Select a group</option>
                {schools.map((school) => (
                  <option value={school} key={school}>{school}</option>
                ))}
              </Select>
              {formData.school === 'Other' && (
                <div style={{ marginTop: '12px' }}>
                  <Label htmlFor="other_group">Please specify your choir group</Label>
                  <Input
                    id="other_group"
                    name="other_group"
                    value={formData.other_group}
                    onChange={handleChange}
                    placeholder="Enter choir group name"
                    required={formData.school === 'Other'}
                  />
                </div>
              )}
            </div>

            <Button type="submit" disabled={status.submitting}>
              {status.submitting ? 'Sending...' : 'Email My Discount Code'}
            </Button>

            {status.error && (
              <StatusMessage error>{status.error}</StatusMessage>
            )}
            {status.success && (
              <StatusMessage>
                {status.message}{' '}
                {status.code && (
                  <>
                    <br />
                    <strong>Code:</strong> {status.code}
                  </>
                )}
              </StatusMessage>
            )}
          </Form>
        </Card>
      </PageWrapper>
    </>
  );
};

export default ChoirDiscount;

